import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtractionSettingsResponse, ProcessingClient } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';

const ExtractionSettingsPanel: React.FC = () => {
  const { t } = useTranslation();
  const client = useMemo(() => new ProcessingClient(getApiBaseUrl(), authenticatedFetch), []);
  const [settings, setSettings] = useState<ExtractionSettingsResponse | null>(null);
  const [threshold, setThreshold] = useState('90');
  const [monthly, setMonthly] = useState('');
  const [batch, setBatch] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const load = useCallback(async () => {
    const data = await client.getApiSettingsExtraction();
    setSettings(data);
    setThreshold(String((data.autoVerifyThreshold ?? 0.9) * 100));
    setMonthly(data.monthlyCapGbp == null ? '' : String(data.monthlyCapGbp));
    setBatch(data.perBatchCapGbp == null ? '' : String(data.perBatchCapGbp));
  }, [client]);
  useEffect(() => { void load().catch(() => setMessage('extractionSettings.error')); }, [load]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!settings?.version) return;
    setBusy(true);
    try {
      await client.putApiSettingsExtraction({ autoVerifyThreshold: Number(threshold) / 100,
        monthlyCapGbp: monthly === '' ? undefined : Number(monthly), perBatchCapGbp: batch === '' ? undefined : Number(batch),
        expectedVersion: settings.version });
      await load();
      setMessage('extractionSettings.saved');
    } catch {
      setMessage('extractionSettings.error');
    } finally { setBusy(false); }
  };
  const keyLabel = (key?: string) => key?.includes('ConfidenceThreshold') ? t('extractionSettings.threshold')
    : key === 'AiMonthlyCostCapGbp' ? t('extractionSettings.monthly') : t('extractionSettings.batch');
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('extractionSettings.title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>{t('extractionSettings.help')}</Typography>
      {message && <Alert severity={message.endsWith('saved') ? 'success' : 'error'} sx={{ mb: 2 }}>{t(message)}</Alert>}
      <Stack component="form" onSubmit={save} spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField label={t('extractionSettings.threshold')} type="number" required value={threshold}
            inputProps={{ min: 0.01, max: 100, step: 0.01 }} onChange={e => setThreshold(e.target.value)} />
          <TextField label={t('extractionSettings.monthly')} type="number" value={monthly}
            inputProps={{ min: 0.01, step: 0.01 }} onChange={e => setMonthly(e.target.value)} />
          <TextField label={t('extractionSettings.batch')} type="number" value={batch}
            inputProps={{ min: 0.01, step: 0.01 }} onChange={e => setBatch(e.target.value)} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button type="submit" disabled={busy || !settings} variant="primary">{t('common.save')}</Button>
          <Button disabled={busy} onClick={() => void load().catch(() => setMessage('extractionSettings.error'))}>{t('common.refresh')}</Button>
        </Stack>
      </Stack>
      {(settings?.history ?? []).length > 0 && <Typography sx={{ mt: 2, fontWeight: 600 }}>{t('extractionSettings.history')}</Typography>}
      {(settings?.history ?? []).map((change, i) => (
        <Typography key={i} variant="textSM" sx={{ display: 'block', mt: 0.5 }}>
          {t('extractionSettings.change', { key: keyLabel(change.key), previous: change.previousValue ?? '—',
            value: change.newValue, date: change.changedAt ? new Date(change.changedAt).toLocaleString() : '', actor: change.actorUserId })}
        </Typography>
      ))}
    </Paper>
  );
};
export default ExtractionSettingsPanel;
